import { motion } from 'framer-motion';
import { Calendar, MapPin, Building } from 'lucide-react';
import { experienceData } from '../data/experience';
import ReactMarkdown from 'react-markdown';

const sortedExperienceData = [
  ...experienceData.filter(exp => exp.current),
  ...experienceData.filter(exp => !exp.current)
];

const ExperiencePage = () => {
  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-12"
      >
        <h1 className="text-4xl font-bold mb-4">Experience</h1>
        <p className="text-xl text-muted-foreground">
          My professional journey across software engineering, product management, and consulting.
        </p>
      </motion.div>

      <div className="max-w-6xl mx-auto">
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-primary-100 dark:bg-primary-900"></div>

          {sortedExperienceData.map((experience, index) => (
            <motion.div
              key={experience.id}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`relative flex items-center mb-12 ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
            >
              {/* Timeline dot */}
              <div className={`absolute left-1/2 transform -translate-x-1/2 w-6 h-6 rounded-full border-4 border-background ${experience.current ? 'bg-green-500 animate-pulse' : 'bg-primary-600'} transition-all duration-300`}></div>

              {/* Content Card */}
              <motion.div
                className={`w-full md:w-1/2 ${index % 2 === 0 ? 'pr-8' : 'pl-8'}`}
                whileHover={{ scale: 1.02 }}
              >
                <div className="card cursor-pointer transition-all duration-300 hover:shadow-lg">
                  <div className="card-content">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4 mt-2">
                      <div className="flex-1 flex items-center gap-3">
                        {/* Company Logo as small icon */}
                        {experience.logo && (
                          <img
                            src={experience.logo}
                            alt={experience.company}
                            className="w-28 h-28 object-contain rounded-md border bg-white p-0.5 shadow-sm"
                          />
                        )}
                        <div>
                          <h3 className="text-xl font-semibold mb-1">{experience.position}</h3>
                          <div className="flex items-center gap-2 text-muted-foreground mb-2">
                            <Building size={16} />
                            <span className="font-medium">{experience.company}</span>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                            <div className="flex items-center gap-1">
                              <Calendar size={14} />
                              <span>{experience.startDate} - {experience.endDate}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <MapPin size={14} />
                              <span>{experience.location}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Only show 'Current' badge if current is true */}
                      {experience.current && (
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">
                          Current
                        </span>
                      )}
                    </div>

                    <div className="text-muted-foreground mb-4 prose prose-sm max-w-none dark:prose-invert">
                      <ReactMarkdown>
                        {experience.description}
                      </ReactMarkdown>
                    </div>

                    {experience.achievements.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-medium mb-2">Key Achievements:</h4>
                        <ul className="space-y-1">
                          {experience.achievements.map((achievement, idx) => (
                            <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                              <span className="text-primary-600 mt-1">•</span>
                              <span>{achievement}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {experience.technologies.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2">Technologies & Skills:</h4>
                        <div className="flex flex-wrap gap-2">
                          {experience.technologies.map((tech, idx) => (
                            <span
                              key={idx}
                              className="bg-secondary-100 dark:bg-secondary-800 text-xs px-2 py-1 rounded"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Add Visit Website button if link exists */}
                    {experience.link && (
                      <div className="mt-4">
                        <a
                          href={experience.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded transition-colors duration-200 shadow-sm"
                        >
                          Visit Website
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ExperiencePage; 